import { Inbox } from "lucide-react";

import { StatusPill, type StatusTone } from "@/components/status-pill";
import type { AdminMcpAuditEntry } from "@/lib/api";

interface Props {
  entries: AdminMcpAuditEntry[];
  loading?: boolean;
}

const OUTCOME_TONE: Record<string, StatusTone> = {
  ok: "green",
  denied: "maroon",
  error: "orange",
};

function OutcomePill({ outcome }: { outcome: string }) {
  // Error outcomes are recorded as `error:<ExceptionClassName>` (e.g.
  // `error:ToolError`), so bucket any `error:*` value to the error tone —
  // an exact-value lookup would miss them and fall back to neutral styling.
  const toneKey = outcome.startsWith("error:") ? "error" : outcome;
  return <StatusPill tone={OUTCOME_TONE[toneKey] ?? "neutral"}>{outcome}</StatusPill>;
}

export function McpAuditTable({ entries, loading }: Props) {
  return (
    <div className="bg-card border-border relative overflow-hidden rounded-lg border shadow-sm">
      {loading && (
        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
          <span className="text-muted text-xs">Loading…</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-warm-gray-01 border-border border-b">
            <tr>
              <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                When
              </th>
              <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                Customer
              </th>
              <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                Person
              </th>
              <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                Tool
              </th>
              <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                Outcome
              </th>
              <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                Detail
              </th>
              <th className="text-muted px-4 py-3 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                Latency
              </th>
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {entries.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center">
                    <Inbox className="text-muted h-6 w-6" />
                    <p className="text-foreground text-sm font-medium">No matching calls</p>
                    <p className="text-muted max-w-xs text-xs">
                      Try widening the time window or clearing the filters above.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-warm-gray-01 transition-colors">
                  <td className="text-muted px-4 py-3 align-middle whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 align-middle">{entry.account_name}</td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex flex-col">
                      <span className="text-foreground">{entry.contact_name ?? "—"}</span>
                      {entry.contact_email && (
                        <span className="text-muted text-xs">{entry.contact_email}</span>
                      )}
                    </div>
                  </td>
                  <td className="text-foreground px-4 py-3 align-middle font-mono text-xs">
                    {entry.tool_name}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <OutcomePill outcome={entry.outcome} />
                  </td>
                  <td className="text-muted px-4 py-3 align-middle">{entry.args_summary ?? "—"}</td>
                  <td className="text-muted px-4 py-3 text-right align-middle whitespace-nowrap">
                    {entry.latency_ms == null ? "—" : `${entry.latency_ms} ms`}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
